import React, { useCallback, useEffect, useMemo } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ArcButton, ArcIconButton, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from "@src/core/ui";
import { setMapCenter } from "@src/core/controllers";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { selectFreehandShapeUponCreation, setFreehandShapeUponCreation, setZoneMapLocationUponCreation, useCreateZoneMutation } from "@src/features/zones/redux";
import { routesConfig } from "@src/core/routes";
import { useDispatch, useSelector } from "react-redux";
import { ICreateZoneStage2Form } from "./types";
import { ZoneTypeName } from "@src/features/zones/config/zones-constants";
import { ZoneShapeType, ZoneType } from "@src/core/features";
import { trackError, toTrackedError } from "@src/core/sentry";
import { usePolygonVertexInputs } from "@src/features/zones/hooks/use-polygon-vertex-inputs";

export interface CreateZoneStage2PageProps {
}

export const CreateZoneStage2Page: React.FC<CreateZoneStage2PageProps> = ({}) => {

  const defaultValues = {
    zoneName: '',
    zoneDescription: '',
    polygonCoordinates: [],
    zoneType: ZoneType.TEMPORARY,
  } as ICreateZoneStage2Form;

  const { control, setValue, formState: { errors }, handleSubmit } = useForm<ICreateZoneStage2Form>({ defaultValues });

  const navigate = useNavigate();
  const [createZone, { isLoading }] = useCreateZoneMutation();

  const dispatch = useDispatch();
  const freehandShapeUponCreation = useSelector(selectFreehandShapeUponCreation);

  const polygonVertices = useMemo(
    () => freehandShapeUponCreation?.polygonCoordinates ?? [],
    [freehandShapeUponCreation?.polygonCoordinates]
  );

  const setPolygonVertex = useCallback(
    (index: number, key: "lat" | "lng", n: number) => {
      const base = freehandShapeUponCreation?.polygonCoordinates ?? [];
      if (!base[index]) return;
      const next = base.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
      next[index] = {
        ...next[index],
        [key]: n,
      };
      dispatch(
        setFreehandShapeUponCreation({
          id: freehandShapeUponCreation?.id ?? 0,
          name: freehandShapeUponCreation?.name ?? "",
          type: freehandShapeUponCreation?.type ?? ZoneType.TEMPORARY,
          polygonCoordinates: next,
        })
      );
    },
    [dispatch, freehandShapeUponCreation?.id, freehandShapeUponCreation?.name, freehandShapeUponCreation?.polygonCoordinates, freehandShapeUponCreation?.type]
  );

  const {
    displayVertexCoord,
    onVertexCoordChange,
    onVertexCoordBlur,
    buildCoordinatesForSubmit,
  } = usePolygonVertexInputs({
    vertexCount: polygonVertices.length,
    setPolygonVertex,
  });

  const removePolygonVertex = useCallback(
    (index: number) => {
      const base = freehandShapeUponCreation?.polygonCoordinates ?? [];
      if (index < 0 || index >= base.length) return;
      const next = base
        .filter((_, j) => j !== index)
        .map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
      dispatch(
        setFreehandShapeUponCreation({
          id: freehandShapeUponCreation?.id ?? 0,
          name: freehandShapeUponCreation?.name ?? "",
          type: freehandShapeUponCreation?.type ?? ZoneType.TEMPORARY,
          polygonCoordinates: next,
        })
      );
    },
    [dispatch, freehandShapeUponCreation?.id, freehandShapeUponCreation?.name, freehandShapeUponCreation?.polygonCoordinates, freehandShapeUponCreation?.type]
  );

  useEffect(() => {
    return () => {
      dispatch(setFreehandShapeUponCreation(null));
    };
  }, []);


  const onCancel =()=> { 
    dispatch(setFreehandShapeUponCreation(null));
    navigate(routesConfig.zones);
  };

  const onClear = () => {
    dispatch(setFreehandShapeUponCreation({
      id: freehandShapeUponCreation?.id || 0,
      name: freehandShapeUponCreation?.name || '',
      type: freehandShapeUponCreation?.type || ZoneType.TEMPORARY,
      polygonCoordinates: [],
    }));
  };

  const onSubmit = async (data: ICreateZoneStage2Form) => {
    try {
      const polygonCoordinates = buildCoordinatesForSubmit(
        freehandShapeUponCreation?.polygonCoordinates ?? []
      );
      const payload = {
        name: data.zoneName,
        description: data.zoneDescription,
        zoneType: data.zoneType,
        isActive: true,
        polygonCoordinates,
        shape: ZoneShapeType.POLYGON,
      };
      const result = await createZone(payload).unwrap();
      navigate(routesConfig.zones); 
      dispatch(setZoneMapLocationUponCreation(null));
    } catch (error: unknown) {
      console.error('Failed to create zone:', error);
      trackError(toTrackedError(error), { code: 'FE_0017', extra: { flow: 'createZoneStage2' } });
    }
  };
    
  const title = 'DRAW ZONE';

  return (
    <ArcStack
      height="100%"
      direction="column"
      minHeight={0}
      sx={{ minHeight: 0, overflow: "hidden" }}
    >
      <ArcStack flexShrink={0} spacing={0}>
        <ArcText.h4 fontWeight={500} marginTop={2}>{title}</ArcText.h4>
        <ArcText.h5 sx={{ color: 'text.secondary', marginTop: 1 }}> Draw a shape on the map to set up a zone. </ArcText.h5>
      </ArcStack>

      <ArcStack
        flex={1}
        minHeight={0}
        spacing={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pb: 1,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <ArcStack marginTop={3} spacing={2}>

          <Controller
            control={control}
            name="zoneType"
            render={({ field: { value, onChange } }) => (
              <ArcOutlinedSelect
                label="Zone Type"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  dispatch(setFreehandShapeUponCreation({
                    name: freehandShapeUponCreation?.name || "",
                    type: e.target.value as ZoneType,
                    polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
                  }));
                }}
                error={Boolean(errors.zoneType)}
                helperText={errors.zoneType?.message}
              >
                {Object.entries(ZoneTypeName).map(([key, name]) => (
                  <ArcMenuItem key={key} value={key}>
                    {name}
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>
            )}
          />

          <Controller
            name="zoneName"
            control={control}
            rules={{
              required: "Zone name is required",
              maxLength: { value: 20, message: "Maximum 20 characters allowed" },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ArcOutlinedInput
                autoComplete="off"
                label="Zone Name"
                value={value || ""}
                error={Boolean(error)}
                helperText={error?.message}
                maxLength={20}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange(val);
                  setValue("zoneName", val);
                  dispatch(setFreehandShapeUponCreation({
                    name: val,
                    type: freehandShapeUponCreation?.type || ZoneType.TEMPORARY,
                    polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
                  }));
         
                }}
              />
            )}
          />

          <Controller
            name="zoneDescription"
            control={control}
            rules={{
              maxLength: { value: 200, message: "Maximum 200 characters allowed" },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ArcOutlinedInput
                autoComplete="off"
                label="Zone Description"
                value={value || ""}
                error={Boolean(error)}
                helperText={error?.message}
                maxLength={200}
                multiline
                onChange={onChange}
              />
            )}
          />

          {polygonVertices.length > 0 && (
            <ArcStack spacing={2} marginTop={2}>
              {polygonVertices.map((pt, i) => (
                <ArcStack key={`vertex-${i}`} spacing={1.5}>
                  <ArcText.h6 color="text.primary" fontSize={14}>
                      Point {i + 1}
                  </ArcText.h6>
                  <ArcStack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <ArcOutlinedInput
                      type="text"
                      inputMode="decimal"
                      label="Latitude"
                      fullWidth
                      sx={{ minWidth: 140, flex: 1 }}
                      value={displayVertexCoord(
                        i,
                        "lat",
                        pt?.lat != null ? Number(pt.lat) : undefined
                      )}
                      onChange={(e) =>
                        onVertexCoordChange(i, "lat", e.target.value)
                      }
                      onBlur={(e) =>
                        onVertexCoordBlur(i, "lat", e.target.value)
                      }
                      onFocus={() => {
                        const lat = Number(pt?.lat);
                        const lng = Number(pt?.lng);
                        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
                        dispatch(setMapCenter({ lat, lng }));
                      }}
                    />
                    <ArcOutlinedInput
                      type="text"
                      inputMode="decimal"
                      label="Longitude"
                      fullWidth
                      sx={{ minWidth: 140, flex: 1 }}
                      value={displayVertexCoord(
                        i,
                        "lng",
                        pt?.lng != null ? Number(pt.lng) : undefined
                      )}
                      onChange={(e) =>
                        onVertexCoordChange(i, "lng", e.target.value)
                      }
                      onBlur={(e) =>
                        onVertexCoordBlur(i, "lng", e.target.value)
                      }
                      onFocus={() => {
                        const lat = Number(pt?.lat);
                        const lng = Number(pt?.lng);
                        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
                        dispatch(setMapCenter({ lat, lng }));
                      }}
                    />
                    <ArcIconButton
                      aria-label={`Delete point ${i + 1}`}
                      sx={{ width: 40, height: 40, flexShrink: 0 }}
                      onClick={() => removePolygonVertex(i)}
                    >
                      <DeleteOutlineIcon color="error" fontSize="small" />
                    </ArcIconButton>
                  </ArcStack>
                </ArcStack>
              ))}
            </ArcStack>
          )}
        </ArcStack>
      </ArcStack>

      <ArcStack flexShrink={0} spacing={2} px={1} pt={2} pb={0.5}>
        <ArcStack direction='row' spacing={2}>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onCancel}> 
          Cancel 
          </ArcButton.ghost>

          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onClear}> 
          Clear Zone
          </ArcButton.ghost>
        </ArcStack>
        <ArcButton.brand 
          fullWidth
          size='large'
          type='submit'
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}> 
          Create
        </ArcButton.brand>
      </ArcStack>
 
    </ArcStack>
  );
};
